import { MobileWaage } from './waage';
import { Logger } from '../helper/app-error-logger';
import { BluetoothSerial } from '@awesome-cordova-plugins/bluetooth-serial/ngx';
import { MyApp } from '../helper/app';
import { Subscription } from 'rxjs';


import { GeraetStatusTyp, WaageProtokollEintrag, WiegedatenEx } from '../model/model';
import { Utils } from '../helper/utils';
import { Wiegedaten } from '../model/swagger-model';
import { WaageBase } from './waage-base';

const log = new Logger('BoscheWaage');

export class BoscheWaage extends WaageBase implements MobileWaage {

    constructor(protected bluetoothSerial: BluetoothSerial) {
        super(log, bluetoothSerial);
    }

    getTyp() {
        return 'bosche';
    }

    async wiegen(nr: number): Promise<boolean> {
        log?.debug('wiegen: ' + nr);

        try {
            await this.pruefeVerbindung('wiegen');

            if (nr == 1) {
                // Erste Wiegung
                await this.senden('WEI.GRO');
            } else if (nr == 2) {
                // Zweite Wiegung
                await this.senden('WEI.GROA');
            }

            return true;
        } catch (err) {
            log.error('wiegen: ' + Utils.getErrorMessage(err), err);
            return false;
        } finally {
            await this.trenneVerbindungBeiBedarf();
        }
    }

    async einzelwiegung(): Promise<WiegedatenEx> {
        throw new Error('BOSCHE: Einzelwiegung nicht verfügbar.');
    }

    async aktiviereWiegemodus(): Promise<boolean> {
        // Wird nicht verwendet.
        return true;
    }

    async tarieren() {
        log?.debug('tarieren');

        try {
            await this.pruefeVerbindung('tarieren');
            await this.senden('WEI.TAR');
        } catch (err) {
            log.error('tarieren: ' + Utils.getErrorMessage(err), err);
        }
    }

    async aufNullSetzen() {
        log?.debug('aufNullSetzen');

        try {
            await this.pruefeVerbindung('aufNullSetzen');
            await this.senden('WEI.ZRP');
        } catch (err) {
            log.error('aufNullSetzen: ' + Utils.getErrorMessage(err), err);
        }
    }

    async stabilIndikatorAbfragen() {
        log?.debug('stabilIndikatorAbfragen');

        try {
            await this.pruefeVerbindung('stabilIndikatorAbfragen');
            await this.senden('WEI.STA');
        } catch (err) {
            log.error('stabilIndikatorAbfragen: ' + Utils.getErrorMessage(err), err);
        }
    }

    async info1() {
        log?.debug('info1');

        try {
            await this.pruefeVerbindung('info1');
            await this.senden('INF.DID');
        } catch (err) {
            log.error('info1: ' + Utils.getErrorMessage(err), err);
        } finally {
            await this.trenneVerbindungBeiBedarf();
        }
    }

    async info2() {
        log?.debug('info2');

        try {
            await this.pruefeVerbindung('info2');
            await this.senden('INF.FVN');
        } catch (err) {
            log.error('info2: ' + Utils.getErrorMessage(err), err);
        }
    }

    async info3() {
        log?.debug('info3');

        try {
            await this.pruefeVerbindung('info3');
            await this.senden('INF.SER');
        } catch (err) {
            log.error('info3: ' + Utils.getErrorMessage(err), err);
        }
    }

    async schreibeAlibieintrag() {
        log?.debug('schreibeAlibieintrag');

        try {
            await this.pruefeVerbindung('schreibeAlibieintrag');
            await this.senden('MOD.ALB');
        } catch (err) {
            log.error('schreibeAlibieintrag: ' + Utils.getErrorMessage(err), err);
        }
    }

    async leseLetzteAlibiNummer(): Promise<number> {
        log?.debug('leseLetzteAlibiNummer');

        try {
            await this.pruefeVerbindung('leseLetzteAlibiNummer');

            if (this.status.getValue() != GeraetStatusTyp.Verbunden) {
                log.warn('leseLetzteAlibiNummer: Waage nicht verbunden');
                return -1;
            }

            return new Promise<number>(async (resolve, reject) => {
                let isResolved = false;
                let subscription: Subscription = null;
                let timeoutId: any = null;

                const myResolveFn = (value) => {
                    if (timeoutId) {
                        clearTimeout(timeoutId);
                    }

                    if (subscription) {
                        subscription.unsubscribe();
                        subscription = null;
                    }

                    if (!isResolved) {
                        isResolved = true;
                        resolve(value);
                    }
                }

                // Timeout nach 10 Sekunden
                timeoutId = setTimeout(() => {
                    log.warn('leseLetzteAlibiNummer: Timeout nach 10 Sekunden');

                    myResolveFn(-1);
                }, 10_000);

                try {
                    await this.senden('MOD.ALE');

                    const retryIntervalId = setInterval(() => {
                        if (isResolved) {
                            clearInterval(retryIntervalId);
                            return;
                        }

                        if (this.lastDataReceived < Date.now() - 2_000) {
                            // Es wurden 2 Sekunden lang keine Daten empfangen.
                            // Schicke die Anfrage erneut.
                            log.warn('leseLetzteAlibiNummer: Sende MOD.ALE Anfrage erneut...');

                            this.senden('MOD.ALE');
                        }
                    }, 3_000);

                    subscription = this.letzteAlibinummerEmpfangen.subscribe({
                        next: (value) => {
                            log?.debug('letzteAlibinummerEmpfangen: ' + value);
                            myResolveFn(value);
                        },
                        error: (err) => {
                            // Das wird wahrscheinlich nie aufgerufen.
                            log.warn('letzteAlibinummerEmpfangen ERROR');
                            myResolveFn(-1);
                        }
                    });
                } catch (err) {
                    log.error('leseLetzteAlibiNummer: Fehler beim Senden: ' + Utils.getErrorMessage(err));
                }
            });
        } catch (err) {
            log.error('leseLetzteAlibiNummer: ' + Utils.getErrorMessage(err), err);
        }

        return 0;
    }

    async leseAlibieintrag(alibiNr: number): Promise<Wiegedaten> {
        log?.debug('leseWiegedatenMitAlibiNr: ' + alibiNr);

        try {
            if (!MyApp.isCordovaAvailable()) {
                await Utils.delay(250);

                let net = Math.round(Math.random() * 2000) / 10;

                return {
                    Netto: net,
                    Wiegenummer: alibiNr,
                    Wiegung1: net,
                    Info: 'Simulation'
                }
            }

            await this.pruefeVerbindung();

            return new Promise<Wiegedaten>(async (resolve, reject) => {
                await this.senden('MOD.ALB' + alibiNr);

                // Timeout nach 10 Sekunden
                const timeoutId = setTimeout(() => {
                    log.warn('leseWiegedatenMitAlibiNr: Timeout nach 10 Sekunden');
                    resolve(null);
                }, 10_000);

                const subscription = this.alibieintragEmpfangen.subscribe({
                    next: (value) => {
                        clearTimeout(timeoutId);
                        subscription.unsubscribe();
                        resolve(value);
                    },
                    error: (err) => {
                        clearTimeout(timeoutId);
                        subscription.unsubscribe();
                        resolve(null);
                    }
                });
            });
        } catch (err) {
            log.error('leseAlibieintrag: ' + Utils.getErrorMessage(err), err);
        }

        return null;
    }

    async waegedatenAbfragen() {
        log?.debug('waegedatenAbfragen');

        try {
            await this.pruefeVerbindung();
            await this.senden('WEI.MDF');
        } catch (err) {
            log.error('waegedatenAbfragen: ' + Utils.getErrorMessage(err), err);
        }
    }

    async aktiviereKontinuierlichesSendenDerWaegedaten() {
        log?.debug('aktiviereKontinuierlichesSendenDerWaegedaten');

        try {
            await this.pruefeVerbindung();
            await this.senden('WEI.ADF');
        } catch (err) {
            log.error('aktiviereKontinuierlichesSendenDerWaegedaten: ' + Utils.getErrorMessage(err), err);
        }
    }

    getDataDelimiter() {
        return '\n';
    }

    async onDataReceived(message: string) {
        try {
            log?.debug('Waage onDataReceived', message);

            this.lastDataReceived = Date.now();

            this.status.next(GeraetStatusTyp.Verbunden);
            this.datenEmpfangen.next();

            // Entferne Zeilenumbrüche (\r) am Ende
            message = message.trim();

            const protokoll = await this.getProtokoll();

            const protokollEintrag: WaageProtokollEintrag = {
                timestamp: Date.now(),
                rohdaten: message,
                typ: 'unbekannt',
                daten: ''
            };

            protokoll.eintraege.push(protokollEintrag);

            this.speichereProtokoll();

            if (message.length < 7) {
                // ungültige Nachricht.
                return;
            }

            // Die ersten 7 Zeichen enthalten den Befehl (Command)
            // z.B. WEI.ZRP oder WEI.GRO
            const command = message.substring(0, 7);

            // Die eigentlichen Nutzdaten (abhängig vom Command)
            const nutzdaten = message.substring(7, message.length - 2);

            // Die letztem beiden Zeichen enthalten die Checksumme
            const checksum = message.substring(message.length - 2, message.length);

            log?.debug('Waage onDataReceived Command: ' + command);
            log?.debug('Waage onDataReceived Nutzdaten: ' + nutzdaten);
            log?.debug('Waage onDataReceived Checksum: ' + checksum);

            // if (!message.startsWith('{')) {
            //     // Verwerfe Nachricht, da nicht vollständig
            //     return;
            // }



            // // Wiededaten Beispiel:
            // // {5#30#1#1915486214#08#02#20#12#57#50##VIE-EN xxx#####110##110##0#######@99}

            // // Entferne die geschweiften Klammern
            // const daten = message.substr(1, message.length - 2);

            // log?.debug('Daten: ' + daten);

            // const posAt = daten.indexOf('@');

            // if (posAt <= 0) {
            //     // Keien Checksumme gefunden. Ignorieren
            //     return;
            // }

            // const nutzdaten = daten.substr(0, posAt);
            // const checksumme = parseInt(daten.substr(posAt + 1), 10);
            // const berechneteChecksum = this.berechneChecksumme(nutzdaten);

            // log?.debug('Daten', {
            //     nutzdaten,
            //     checksumme,
            //     berechneteChecksum
            // });

            // if (checksumme != berechneteChecksum) {
            //     log.warn('Checksumme ungültig. Egal.');
            //     return;
            // }

            // const parts = daten.split('#');

            // const empfaenger = parseInt(parts[0], 10);
            // const command = parseInt(parts[1], 10);
            // const absender = parseInt(parts[2], 10);

            // if (empfaenger != BOARDCOMPUTER) {
            //     // 5 ist der Boardcomputer, also wir.
            //     // Alles für andere Empfänger ignorieren
            //     log.warn('Ungültiger Empfänger: ' + empfaenger);
            //     return;
            // }

            // if (absender != WAAGE) {
            //     log.warn('Ungültiger Absender: ' + absender);
            //     return;
            // }

            switch (command) {
                case 'WEI.GRO':
                    this.verarbeiteWiegedaten(nutzdaten, protokollEintrag);
                    break;

                case 'WEI.STA':
                    this.verarbeiteWaageStabilIndikator(nutzdaten, protokollEintrag);
                    break;

                case 'WEI.ZRP':
                    // Waage wurde auf 0 gesetzt
                    protokollEintrag.daten = 'ACK ZRP';
                    protokollEintrag.typ = 'ACK';
                    break;

                case 'MOD.ALE':
                    protokollEintrag.daten = nutzdaten;
                    protokollEintrag.typ = 'ALE';
                    this.letzteAlibinummerEmpfangen.next(Utils.parseInt(nutzdaten));

                    this.speichereProtokoll();
                    break;

                case 'MOD.ALB':
                    this.verarbeiteAlibieintrag(nutzdaten, protokollEintrag);
                    break;

                default:
                    protokollEintrag.daten = nutzdaten;
                    protokollEintrag.typ = command;
            }

        } catch (err) {
            log.error('onDataReceived', err);
            this.error.next(Utils.getErrorMessage(err));
        }
    }

    verarbeiteAlibieintrag(nutzdaten: string, protokollEintrag: WaageProtokollEintrag) {
        log?.debug('verarbeiteAlibieintrag: ' + nutzdaten);

        // Beispiel: MOD.ALB : ScaleNr: 0000;AlibiNr:      11;Date: 00.00.2000;Time: 00:02:22;GRO:+    1.4;NET:+    0.0; TARE:+    0.0;0d
        // D.h. Nutzdaten: ' : ScaleNr: 0000;AlibiNr:      11;Date: 00.00.2000;Time: 00:02:22;GRO:+    1.4;NET:+    0.0; TARE:+    0.0;'

        let wiegedaten: Wiegedaten = {
            Info: '',
        };

        let infoLines: string[] = [];

        const parts = Utils.split([';'], nutzdaten);

        for (const str of parts) {
            const kv = Utils.split([':'], str);

            if (kv.length === 2) {
                let key = kv[0].trim().toUpperCase();
                let value = kv[1].trim();

                switch (key) {
                    case 'CH':
                        infoLines.push('CH: ' + value);
                        break;

                    case 'ALIBINR':
                        wiegedaten.Wiegenummer = Utils.parseInt(value);
                        break;

                    case 'GRO':
                        // Die Bosche-Waage liefert hier immer das Netto-Gewicht als Bruttogewicht..
                        wiegedaten.Netto = this.parseGewicht(value);
                        wiegedaten.Wiegung1 = this.parseGewicht(value);
                        infoLines.push('Gro: ' + value);
                        break;

                    case 'NET':
                        infoLines.push('Net: ' + value);
                        break;

                    case 'TARE':
                        infoLines.push('Tara: ' + value);
                        break;

                    default:
                        infoLines.push(key + ': ' + value);
                        break;
                }
            } else {
                infoLines.push(str);
            }
        }

        wiegedaten.Info = infoLines.join(', ');

        this.alibieintragEmpfangen.next(wiegedaten);

        protokollEintrag.daten = nutzdaten;
        protokollEintrag.typ = 'ALB';

        this.speichereProtokoll();
    }

    parseGewicht(value: string): number {
        // Beispiel: '+    1.4' oder '-    0.0'
        const vorzeichen = value.substring(0, 1);
        let strGewicht = '';

        if (vorzeichen == '+' || vorzeichen == '-') {
            // Gültiges Vorzeichen gefunden
            strGewicht = value.substring(1).trim();
        } else {
            strGewicht = value.trim();
        }

        let gewicht = Utils.parseFloat(strGewicht);

        if (vorzeichen === '-') {
            gewicht = -gewicht;
        }

        return gewicht;
    }

    async verarbeiteWaageStabilIndikator(nutzdaten: string, protokollEintrag: WaageProtokollEintrag) {
        log?.debug('verarbeiteWaageStabilIndikator: ' + nutzdaten);

        protokollEintrag.daten = nutzdaten;
        protokollEintrag.typ = 'ST';

        this.speichereProtokoll();

    }

    async verarbeiteWiegedaten(nutzdaten: string, protokollEintrag: WaageProtokollEintrag) {
        log?.debug('verarbeiteWiegedaten: ' + nutzdaten);

        // Als Antwort kommt z.B.:
        // WEI.GRO+    0.02a
        // WEI.GROA:+    0.051

        // Prüfe ob die Antwort mit Angabe des Kanals (A / B) gesendet wurde:
        let kanal = 'A';

        if (nutzdaten.startsWith('A:') || nutzdaten.startsWith('B:')) {
            // Kanal A oder B
            kanal = nutzdaten.substring(0, 1);
            nutzdaten = nutzdaten.substring(2);
        }

        const vorzeichen = nutzdaten.substring(0, 1);
        const strGewicht = nutzdaten.substring(1);

        let brutto = Utils.parseFloat(strGewicht);

        if (vorzeichen == '-') {
            brutto = -brutto;
        }

        let tara = 0;
        let netto = brutto;

        const alibinummer1 = 0;

        const wiegedaten: Wiegedaten = {
            Wiegenummer: alibinummer1,
            Wiegenummer2: null,
            Datum: Utils.nowIsoDateString(),
            Wiegung1: brutto,
            Wiegung2: null,
            Netto: brutto,
            Info: nutzdaten
        };

        log?.info('Wiegedaten gelesen', wiegedaten);

        // Wiegung speichern
        this.wiegedatenEmpfangen.next(wiegedaten);

        protokollEintrag.daten = `Nr. ${alibinummer1}, Datum: ${Utils.nowGermanDateTimeString()}, Brutto: ${brutto}, Tara: ${tara}, Netto: ${netto}`;
        protokollEintrag.typ = 'wiegung';

        this.speichereProtokoll();
    }

    async syncUhr() {
        log?.debug('syncUhr');
    }

    async senden(nutzdaten: string) {
        const message = nutzdaten + '\r\n';

        log?.debug('senden: ' + message);

        const protokoll = await this.getProtokoll();

        protokoll.eintraege.push({
            daten: 'SEND: ' + message,
            typ: 'senden',
            timestamp: Date.now(),
            rohdaten: message
        });

        this.bluetoothSerial.write(message);
    }

    berechneChecksumme(nutzdaten: string): number {
        let checksum = 0;

        for (const c of nutzdaten) {
            checksum += c.charCodeAt(0);
        }

        return checksum % 256;
    }
}
